import React, { useState, useEffect, useRef, Fragment } from 'react';
import api from '../services/api';
import { Switch } from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser, selectUser, setLanguage } from '../features/userSlice';
import { Link, useNavigate } from 'react-router-dom';
import i18n from '../i18n';
import { Filesystem, Directory } from '@capacitor/filesystem'; // Aggiungi questo import
import { Capacitor } from '@capacitor/core';
import {
  FiUser, FiShield, FiBell, FiUpload, FiDownload, FiTrash2, 
  FiAlertTriangle, FiCheckCircle, FiXCircle, FiGift, FiMail, 
  FiEye, FiEyeOff, FiFacebook, FiInstagram, FiMessageCircle, FiSend
} from 'react-icons/fi';
import { Trans, useTranslation } from 'react-i18next';
import SettingsTeam from './SettingsTeam';
 
const Toast = ({ toasts, removeToast }) => (
  <div className="fixed top-5 right-5 z-50 w-full max-w-sm space-y-3">
    {toasts.map((t) => (
      <div
        key={t.id}
        className={`relative flex items-center gap-4 w-full px-4 py-3 rounded-lg shadow-lg text-white transform transition-all duration-300 animate-slide-in-right ${t.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}
      > 
        {t.type === 'error' ? <FiXCircle size={20} /> : <FiCheckCircle size={20} />}
        <span>{t.message}</span>
        <button
          onClick={() => removeToast(t.id)} 
          className="absolute top-1 right-2 text-white/80 hover:text-white font-bold text-xl"
        >
          &times;
        </button>
      </div>
    ))}
  </div>
);
 
const ConfirmModal = ({ show, title, message, onConfirm, onCancel }) => {
  const { t } = useTranslation();
 
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 animate-fade-in">
      <div className="rounded-xl shadow-2xl p-6 sm:p-8 max-w-md w-full mx-4 text-center space-y-4 transform transition-all duration-300 animate-scale-up">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
          <FiAlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-500">{message}</p>
        <div className="flex justify-center gap-4 pt-4">
          <button
            onClick={onCancel} 
            className="px-6 py-2 bg-slate-200 text-slate-700 rounded-md font-semibold hover:bg-slate-300 transition-colors"
          >
            {t('UserProfile.cancel')}
          </button>
          <button
            onClick={onConfirm} 
            className="px-6 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 transition-colors"
          >
            {t('UserProfile.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
 
const SettingsCard = ({ title, icon, children }) => (
  <div className="rounded-lg shadow-md overflow-hidden">
    <div className="p-6 border-b border-slate-200">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="text-xl font-semibold text-slate-800">{title}</h3>
      </div>
    </div>
    <div className="p-6 space-y-6">
      {children}
    </div>
  </div>
);
 
const InputField = ({ id, label, type = "text", value, onChange, required = false, placeholder = "" }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-black"
    />
  </div>
);
 
const SocialInputField = ({ id, label, icon, value, onChange, placeholder, prefix }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-600 mb-1">{label}</label>
    <div className="flex rounded-md shadow-sm">
      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-slate-300 bg-slate-50 text-slate-500 sm:text-sm">
        {icon}
        <span className="ml-1 hidden sm:inline">{prefix}</span>
      </span>
      <input
        type="text"
        id={id}
        value={value}
        onChange={onChange}
        className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-slate-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
        placeholder={placeholder}
      />
    </div>
  </div>
);
 
const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profilo');
  const [name, setName] = useState('');
  const [language, setLanguageLocal] = useState('');
  const [email, setEmail] = useState('');
  const [avatar, setAvatar] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [toasts, setToasts] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [passwordForEmail, setPasswordForEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmNewEmail, setConfirmNewEmail] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailFeedingNotifications, setEmailFeedingNotifications] = useState(true);
  const [notificationMsg, setNotificationMsg] = useState('');
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [referralError, setReferralError] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [bulkFilters, setBulkFilters] = useState('{ "subscription.plan": "premium", "language": "it" }');
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkHtml, setBulkHtml] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkEmails, setBulkEmails] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const avatarInputRef = useRef(null);
  const [isPublic, setIsPublic] = useState(false);
  const loggedInUser = useSelector(selectUser);
  const [socialsFacebook, setSocialsFacebook] = useState('');
  const [socialsInstagram, setSocialsInstagram] = useState('');
  const isDelegate = !!localStorage.getItem('operateAsId');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
  const nameRegex = /^[a-zA-Z0-9\s]{2,}$/;
  const socialUsernameRegex = /^[a-zA-Z0-9_.-]+$/;
 
  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 4000);
  };
  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));
 
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#referral') {
      setActiveTab('inviti');
    }
  }, []);
 
  useEffect(() => {
    if (avatar instanceof File) {
      const objectUrl = URL.createObjectURL(avatar);
      setAvatarPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (typeof avatar === 'string' && avatar.trim() !== '') {
      const isAbsolute = avatar.startsWith('http://') || avatar.startsWith('https://');
      setAvatarPreview(isAbsolute ? avatar : process.env.REACT_APP_BACKEND_URL_IMAGE + avatar);
    } else {
      setAvatarPreview('');
    }
  }, [avatar]);
 
  const handleSendBulkEmail = async (e) => {
    e.preventDefault();
    setSendingBulk(true);
    setBulkResult(null);
 
    let filtersObj = {};
    try {
      filtersObj = JSON.parse(bulkFilters);
    } catch {
      addToast(t('UserProfile.invalidJsonFilters'), "error");
      setSendingBulk(false);
      return;
    }
 
    if (!bulkSubject || !bulkHtml) {
      addToast(t('UserProfile.subjectHtmlRequired'), "error");
      setSendingBulk(false);
      return;
    }
    const emailsArray = bulkEmails
      .split(',')
      .map(e => e.trim())
      .filter(e => emailRegex.test(e)); 
 
    if (bulkEmails && emailsArray.length === 0) {
      addToast(t('UserProfile.invalidEmailFormat'), "error");
      setSendingBulk(false);
      return;
    }
    try {
      const { data } = await api.post('/user/admin/send-bulk-email', {
        filters: filtersObj,
        emails: emailsArray,
        subject: bulkSubject,
        html: bulkHtml,
        text: bulkText
      });
      setBulkResult(data);
      addToast(t('UserProfile.emailsSent', { sent: data.sent, total: data.total }), 'success');
    } catch (err) {
      addToast(err.response?.data?.error || t('UserProfile.bulkEmailError'), "error");
    } finally {
      setSendingBulk(false);
    }
  };
 
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await api.get('/v1/me');
        setUser(data);
        setName(data.name);
        setLanguageLocal(data.language);
        setEmail(data.email);
        setAvatar(data.avatar);
        setAddress(data.address || '');
        setPhoneNumber(data.phoneNumber || '');
        setSocialsFacebook(data.socials?.facebook || '');
        setSocialsInstagram(data.socials?.instagram || '');
        setIsPublic(data.isPublic || false);
        setEmailFeedingNotifications(data.emailFeedingNotifications ?? true);
      } catch {
        addToast(t('UserProfile.errorProfile'), 'error');
      }
    };
    const fetchReferralLink = async () => {
      try {
        const { data } = await api.get('/user/referral-link');
        setReferralLink(data.referralLink);
      } catch (err) {
        setReferralError(err.response?.data?.message || t('UserProfile.referralError'));
      }
    };
    fetchUser();
    fetchReferralLink();
  }, []);
 
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
 
    const trimmedName = name.trim();
    if (!nameRegex.test(trimmedName)) {
      addToast(t('UserProfile.nameNumbers'), 'error');
      return;
    }
    if (trimmedName.length < 3) {
      addToast(t('UserProfile.nameCharacter'), 'error');
      return;
    }
    const phoneRegex = /^\+?[0-9\s\-]{7,15}$/;
    if (phoneNumber && !phoneRegex.test(phoneNumber.trim())) {
      addToast(t('UserProfile.invalidPhone'), 'error');
      return;
    }
 
    if (socialsFacebook) {
      const cleanFB = socialsFacebook.trim().replace(/^(https?:\/\/)?(www\.)?facebook\.com\//i, '').replace(/\/$/, '');
      if (!socialUsernameRegex.test(cleanFB)) {
        addToast(t('UserProfile.invalidFacebook'), 'error');
        return;
      }
    }
 
    if (socialsInstagram) {
      const cleanIG = socialsInstagram.trim().replace(/^(https?:\/\/)?(www\.)?instagram\.com\//i, '').replace(/\/$/, '');
      if (!socialUsernameRegex.test(cleanIG)) {
        addToast(t('UserProfile.invalidInstagram'), 'error');
        return;
      }
    }
 
    try {
      const formData = new FormData();
      formData.append('name', trimmedName);
      formData.append('language', language);
      formData.append('address', address.trim());
      formData.append('phoneNumber', phoneNumber.trim());
      formData.append('isPublic', isPublic);
      formData.append('socialsFacebook', socialsFacebook.trim());
      formData.append('socialsInstagram', socialsInstagram.trim());
      if (avatar instanceof File) {
        formData.append('avatar', avatar);
      }
 
      const { data } = await api.put(`/user/${user._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
 
      setUser(data);
      dispatch(setLanguage(data.language));
      i18n.changeLanguage(data.language);
      addToast(t('UserProfile.successProfileUpdate'), 'success');
    } catch(err) {
      addToast(err.response?.data?.message || t('UserProfile.errorProfileUpdate'), 'error'); 
    }
  };
 
  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!emailRegex.test(newEmail.trim())) {
      setEmailError(t('UserProfile.emailInvalid'));
      return;
    }
    if (newEmail !== confirmNewEmail) {
      setEmailError(t('UserProfile.emailError'));
      return;
    }
    try {
      const { data } = await api.post('/v1/change-email', { newEmail, password: passwordForEmail });
      setEmail(data.newEmail);
      addToast(t('UserProfile.successEmailChange'), 'success');
      setNewEmail('');
      setConfirmNewEmail('');
      setPasswordForEmail('');
      if (data.forceLogout) {
        await api.post('/v1/logout', null, { withCredentials: true });
        dispatch(logoutUser());
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        navigate('/verify-email', { state: { email: newEmail } });
      }
    } catch (err) {
      setEmailError(err.response?.data?.message || t('UserProfile.EmailGenericError'));
      addToast(t('UserProfile.errorEmailChange'), 'error');
    }
  };
 
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (!passwordRegex.test(newPassword)) {
      setPasswordError(t('UserProfile.PasswordInvalid'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('UserProfile.PasswordMatch'));
      return;
    }
    try {
      await api.post('/v1/change-password', { oldPassword, newPassword, confirmPassword });
      addToast(t('UserProfile.successPasswordChange'), 'success');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.message || t('UserProfile.PasswordError'));
      addToast(t('UserProfile.errorPasswordChange'), 'error');
    }
  };
 
  const confirmDeleteAccount = async () => {
    try {
      await api.delete(`/user/${user._id}`);
      dispatch(logoutUser());
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      navigate('/login');
    } catch {
      addToast(t('UserProfile.deleteUserError'), 'error');
    }
  };
 
  const handleNotificationSave = async () => {
    try {
      await api.patch(`/user/users/email-settings/${user._id}`, { receiveFeedingEmails: emailFeedingNotifications });
      setNotificationMsg(t('UserProfile.successNotification'));
      addToast(t('UserProfile.successNotificationSave'), 'success');
    } catch {
      setNotificationMsg(t('UserProfile.errorNotification'));
      addToast(t('UserProfile.errorNotificationSave'), 'error');
    }
  };
 
 const handleExportExcel = async () => {
  try {
    const response = await api.get(`reptile/export/reptiles/${user._id}`, { 
      responseType: 'blob' 
    });

    // Convertiamo il blob in formato Base64 per Capacitor
    const reader = new FileReader();
    reader.readAsDataURL(response.data);
    reader.onloadend = async () => {
      const base64Data = reader.result.split(',')[1];

      if (Capacitor.isNativePlatform()) {
        // --- LOGICA PER MOBILE ---
        try {
          const fileName = 'reptile_data.xlsx';
          const savedFile = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.Documents, // Salva nella cartella documenti dell'app
          });
          
          addToast(t('UserProfile.downloadSuccess') + ": " + savedFile.uri);
        } catch (err) {
          console.error("Errore salvataggio mobile:", err);
          addToast(t('UserProfile.downloadError'), 'error');
        }
      } else {
        // --- LOGICA ORIGINALE (WEB) ---
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'reptile_data.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        addToast(t('UserProfile.downloadSuccess'));
      }
    };
  } catch (err) {
    console.error(err);
    addToast(t('UserProfile.downloadError'), 'error');
  }
};
  const handleCopyLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2500); 
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      addToast(t('UserProfile.copyError'), 'error');
    });
  };

  if (isDelegate) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-xl border border-slate-200 max-w-md w-full mx-4">
          <FiAlertTriangle className="mx-auto text-yellow-500 w-16 h-16 mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('UserProfile.limitedAccess')}</h2>
          <p className="text-slate-600 mb-6">
            {t('UserProfile.delegateAccessWarning')}
          </p>
          <Link
            to="/dashboard"
            className="inline-block bg-indigo-600 text-white py-2 px-6 rounded-md font-semibold hover:bg-indigo-700 transition-colors"
          > 
            {t('UserProfile.backToDashboard')}
          </Link>
        </div>
      </div>
    );
  }

  if (!user) return <div className="flex items-center justify-center h-screen text-slate-500">{t('UserProfile.loadingProfile')}</div>;
  
  const canBePublic = true;
  const referralCount = user.referralCount || 0;
  const progressPercentage = Math.min((referralCount / 3) * 100, 100);
 
  return (
    <div className="min-h-screen">
      <Toast toasts={toasts} removeToast={removeToast} />
      <ConfirmModal
        show={showDeleteModal}
        title={t('UserProfile.confirmDeletion')}
        message={t('UserProfile.confirmDeletionMsg')}
        onConfirm={() => {
          setShowDeleteModal(false);
          confirmDeleteAccount();
        }}
        onCancel={() => setShowDeleteModal(false)}
      />
 
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800">{t('UserProfile.userProfile')}</h1>
          <Link to="/dashboard" className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors">
            {t('UserProfile.backToDashboard')}
          </Link>
        </div>
 
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Menu Laterale */}
          <aside className="lg:col-span-1 space-y-4">
            <div className="rounded-lg shadow-md p-6 text-center mb-6">
              <div className="relative w-32 h-32 mx-auto group">
                <img
                  src={avatarPreview || '/images/default_avatar.png'}
                  alt="Avatar"
                  className="w-full h-full rounded-full object-cover border-4 border-slate-200"
                />
                <div
                  className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={() => avatarInputRef.current?.click()}
                  aria-hidden="true"
                > 
                  <FiUpload className="text-white h-8 w-8" />
                </div>
 
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-9 h-9 bg-indigo-600 rounded-full flex items-center justify-center text-white shadow-md hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  aria-label={t('UserProfile.changeAvatar')}
                > 
                  <FiUpload className="w-5 h-5" />
                </button>
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => setAvatar(e.target.files[0])}
                className="hidden"
              />
              <h2 className="mt-4 text-xl font-bold text-slate-800">{name}</h2>
              <p className="text-sm text-slate-500">{email}</p>
            </div>
 
            <nav className="flex flex-col space-y-1">
              {[
                { id: 'profilo', label: t('UserProfile.profileInfo'), icon: <FiUser /> },
                { id: 'sicurezza', label: t('UserProfile.security'), icon: <FiShield /> },
                { id: 'notifiche', label: t('UserProfile.notifications'), icon: <FiBell /> },
                { id: 'inviti', label: t('UserProfile.referralTitle'), icon: <FiGift /> },
                ...(user.role === 'admin' ? [{ id: 'admin', label: t('UserProfile.adminBulkEmail'), icon: <FiMail /> }] : [])
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center gap-3 px-4 py-3 text-left rounded-md transition-colors font-medium ${activeTab === item.id ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border-l-4 border-transparent'}`}
                > 
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </nav>
            {/* Esportazione separata dal menu principale */}
            <div className="pt-6 border-t border-slate-200 mt-6">
              <button onClick={handleExportExcel} className="w-full bg-green-600 text-white py-2 px-4 rounded-md font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
                <FiDownload />
                {t('UserProfile.downloadExcel')}
              </button>
            </div>
          </aside>
 
          {/* Area Contenuto */}
          <main className="lg:col-span-3 space-y-8">
            {/* TAB PROFILO */}
            {activeTab === 'profilo' && (
              <SettingsCard title={t('UserProfile.profileInfo')} icon={<FiUser className="text-indigo-500 w-6 h-6" />}>
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField id="name" label={t('UserProfile.name')} value={name} onChange={(e) => setName(e.target.value)} required />
                    <InputField
                      id="phoneNumber"
                      label={t('UserProfile.phoneNumber')}
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+39 333 1234567"
                    />
                  </div>
                  <InputField
                    id="address"
                    label={t('UserProfile.address')}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={t('UserProfile.addressPlaceholder')}
                  />
 
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SocialInputField
                      id="socialsFacebook"
                      label="Facebook"
                      icon={<FiFacebook />}
                      prefix="facebook.com/"
                      value={socialsFacebook}
                      onChange={(e) => setSocialsFacebook(e.target.value)}
                      placeholder="username"
                    />
                    <SocialInputField
                      id="socialsInstagram"
                      label="Instagram"
                      icon={<FiInstagram />}
                      prefix="instagram.com/"
                      value={socialsInstagram}
                      onChange={(e) => setSocialsInstagram(e.target.value)}
                      placeholder="username"
                    />
                  </div>
 
                  <div className="p-4 bg-slate-100 rounded-lg border border-slate-200">
                    <Switch.Group as="div" className="flex items-center justify-between">
                      <span className="flex-grow flex flex-col pr-3">
                        <Switch.Label as="span" className={`text-sm font-medium ${!canBePublic ? 'text-slate-400' : 'text-slate-900'}`} passive>
                          {t('UserProfile.publicProfile')}
                          {isPublic ? <FiEye className="inline w-4 h-4 ml-1.5 text-indigo-600"/> : <FiEyeOff className="inline w-4 h-4 ml-1.5 text-slate-500"/>}
                        </Switch.Label>
                        <Switch.Description as="span" className={`text-xs ${!canBePublic ? 'text-slate-400' : 'text-slate-500'}`}>
                          {t('UserProfile.publicProfileDesc')}
                        </Switch.Description>
                      </span>
                      <Switch
                        checked={isPublic}
                        onChange={setIsPublic}
                        disabled={!canBePublic}
                        className={`${isPublic ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                      > 
                        <span aria-hidden="true" className={`${isPublic ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} />
                      </Switch>
                    </Switch.Group>
                    {!canBePublic && (
                      <p className="mt-3 text-xs text-red-600 border-t border-slate-300 pt-3">
                        <Trans i18nKey="UserProfile.publicProfileDisabled" components={[<Link to="/pricing" className="underline font-semibold" key="1"/>]} />
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="language" className="block text-sm font-medium text-slate-600 mb-1">{t('UserProfile.language')}</label>
                    <select id="language" value={language} onChange={(e) => setLanguageLocal(e.target.value)} required className="w-full md:w-1/2 border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white text-black">
                      <option value="it">Italiano</option>
                      <option value="en">English</option>
                      <option value="fr">Français</option>
    <option value="de">Deutsch</option>
    
                    </select>
                  </div>
                  <button type="submit" className="bg-green-600 text-white py-2 px-6 rounded-md font-semibold hover:bg-green-700 transition-colors">
                    {t('UserProfile.updateProfile')}
                  </button>
                </form>
                {user?.subscription?.plan === 'BREEDER' && (
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <SettingsTeam />
                  </div>
                )}
              </SettingsCard>
            )}
 
            {/* TAB SICUREZZA */}
            {activeTab === 'sicurezza' && (
              <> 
                <SettingsCard title={t('UserProfile.security')} icon={<FiShield className="text-indigo-500 w-6 h-6" />}>
                  <form onSubmit={handleChangeEmail} className="space-y-4 border-b border-slate-200 pb-6">
                    <h4 className="font-semibold text-slate-700">{t('UserProfile.changeEmail')}</h4>
                    <InputField id="newEmail" label={t('UserProfile.newEmail')} type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                    <InputField id="confirmNewEmail" label={t('UserProfile.newEmail')} type="email" value={confirmNewEmail} onChange={(e) => setConfirmNewEmail(e.target.value)} />
                    <InputField id="passwordForEmail" label={t('UserProfile.confirmNewEmail')} type="password" value={passwordForEmail} onChange={(e) => setPasswordForEmail(e.target.value)} placeholder="••••••••" />
                    {emailError && <p className="text-sm text-red-600">{emailError}</p>}
                    <button type="submit" className="w-full sm:w-auto bg-green-700 text-white py-2 px-4 rounded-md font-semibold hover:bg-green-800 transition-colors">{t('UserProfile.submitEmailChange')} </button>
                  </form>
                  <form onSubmit={handleChangePassword} className="space-y-4 pt-6">
                    <h4 className="font-semibold text-slate-700">{t('UserProfile.changePassword')}</h4>
                    <InputField id="oldPassword" label={t('UserProfile.currentPassword')} type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} placeholder="••••••••" />
                    <InputField id="newPassword" label={t('UserProfile.newPassword')} type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
                    <InputField id="confirmPassword" label={t('UserProfile.confirmPassword')} type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
                    {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
                    <button type="submit" className="w-full sm:w-auto bg-green-700 text-white py-2 px-4 rounded-md font-semibold hover:bg-green-800 transition-colors">{t('UserProfile.changePassword')}</button>
                  </form>
                </SettingsCard>
 
                <div className="bg-white rounded-lg shadow-md border-2 border-red-200 mt-8">
                  <div className="p-6 border-b border-red-200">
                    <div className="flex items-center gap-3">
                      <FiTrash2 className="text-red-500 w-6 h-6" />
                      <h3 className="text-xl font-semibold text-red-700">{t('UserProfile.deleteAccount')}</h3>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-slate-600">
                      {t('UserProfile.deleteAccountInfo')}
                    </p>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="w-full sm:w-auto whitespace-nowrap bg-red-600 text-white py-2 px-6 rounded-md font-semibold hover:bg-red-700 transition-colors"
                    > 
                      {t('UserProfile.deleteMyAccount')}
                    </button>
                  </div>
                </div>
              </>
            )}
 
            {/* TAB NOTIFICHE */}
            {activeTab === 'notifiche' && (
              <SettingsCard title={t('UserProfile.notifications')} icon={<FiBell className="text-indigo-500 w-6 h-6" />}>
                <div className="flex items-center justify-between">
                  <label htmlFor="emailFeedingToggle" className="text-sm font-medium text-slate-700">
                    {t('UserProfile.feedingEmails')}
                  </label>
                  <input
                    id="emailFeedingToggle"
                    type="checkbox"
                    checked={emailFeedingNotifications}
                    onChange={(e) => {
                      const allowedPlans = ['BREEDER', 'APPRENTICE', 'PRACTITIONER'];
                      if (!allowedPlans.includes(user.subscription?.plan)) {
                        addToast(t('UserProfile.premiumFeature2'), 'error');
                        return;
                      }
                      setEmailFeedingNotifications(e.target.checked);
                    }}
                    disabled={!['BREEDER', 'APPRENTICE', 'PRACTITIONER'].includes(user.subscription?.plan)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </div>
                {user.subscription?.plan !== 'BREEDER' && user.subscription?.plan !== 'APPRENTICE' && user.subscription?.plan !== 'PRACTITIONER' && (
                  <p className="text-xs text-red-600 mt-1">
                    <Trans i18nKey="UserProfile.premiumFeature" components={[<Link to="/pricing" className="underline font-semibold" key="1" />]} />
                  </p>
                )}
                <div className="pt-4 mt-4 border-t border-slate-100">
                  <button onClick={handleNotificationSave} className="bg-green-600 text-white py-2 px-4 rounded-md font-semibold hover:bg-green-700 transition-colors">
                    {t('UserProfile.savePreferences')}
                  </button>
                </div>
              </SettingsCard>
            )}
            {/* TAB INVITI */}
            {activeTab === 'inviti' && (
              <SettingsCard title={t('UserProfile.referralTitle')} icon={<FiGift className="text-indigo-500 w-6 h-6" />}>
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-lg shadow-sm">
                    <div className="flex items-center gap-4 mb-2">
                      <FiGift className="w-8 h-8 opacity-80" />
                      <h4 className="text-lg font-bold">{t('UserProfile.getDiscount')}</h4>
                    </div>
                    <p className="text-sm opacity-90 mb-4">
                      <Trans i18nKey="UserProfile.referralPromoDesc" components={[<strong key="1" />]} />
                    </p>
                    {/* Progress Bar Premi */}
                    <div className="bg-white/20 rounded-full h-4 w-full overflow-hidden mb-2 relative">
                      <div 
                        className="bg-green-400 h-full transition-all duration-500" 
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs font-semibold">
                      <span>{t('UserProfile.referralsCount', { count: referralCount })}</span>
                      <span>{t('UserProfile.referralGoal')}</span>
                    </div>
                  </div>
 
                  {referralLink && (
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">{t('UserProfile.yourPersonalLink')}</label>
                      <div className="flex flex-col sm:flex-row items-stretch gap-2">
                        <input
                          type="text"
                          readOnly
                          value={referralLink}
                          className="w-full border-slate-300 rounded-md shadow-sm bg-slate-50 text-slate-600 select-all px-3 py-2"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleCopyLink}
                            className={`px-4 py-2 rounded-md font-semibold transition-colors flex-1 sm:flex-none text-white whitespace-nowrap ${isCopied ? 'bg-green-500' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                          > 
                            {isCopied ? t('UserProfile.copied') : t('UserProfile.copy')}
                          </button>
                          {/* Pulsanti Condivisione Rapida */}
                          <a 
                            href={`https://wa.me/?text=${encodeURIComponent(t('UserProfile.signUpViaMyLink') + referralLink)}`} 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center p-2 rounded-md bg-[#25D366] text-white hover:bg-[#1EBE5D] transition-colors"
                            title={t('UserProfile.shareWhatsApp')}
                          > 
                            <FiMessageCircle className="w-5 h-5" />
                          </a>
                          <a 
                            href={`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(t('UserProfile.signUpViaMyLinkExclaim'))}`} 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center p-2 rounded-md bg-[#0088cc] text-white hover:bg-[#0077b5] transition-colors"
                            title={t('UserProfile.shareTelegram')}
                          > 
                            <FiSend className="w-5 h-5" />
                          </a>
                          <a 
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center p-2 rounded-md bg-[#1877F2] text-white hover:bg-[#166FE5] transition-colors"
                            title={t('UserProfile.shareFacebook')}
                          > 
                            <FiFacebook className="w-5 h-5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
 
                  {user.hasReferred && (
                    <div className="flex items-center gap-2 text-green-700 text-sm font-bold bg-green-50 p-4 rounded-md border border-green-200">
                      <FiCheckCircle className="w-5 h-5" />
                      {t('UserProfile.rewardReceived')}
                    </div>
                  )}
 
                  {referralError && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md text-sm">
                      {referralError}
                    </div>
                  )}
                </div>
              </SettingsCard>
            )}
 
            {/* TAB ADMIN BULK */}
            {activeTab === 'admin' && user.role === 'admin' && (
              <SettingsCard title={t('UserProfile.adminBulkEmail')} icon={<FiMail className="text-indigo-500 w-6 h-6" />}>
                <form onSubmit={handleSendBulkEmail} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">{t('UserProfile.filtersJson')}</label>
                    <textarea
                      value={bulkFilters}
                      onChange={(e) => setBulkFilters(e.target.value)}
                      placeholder='{ "subscription.plan": "premium", "language": "it" }'
                      className="w-full border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white text-black p-2 h-24"
                    />
                  </div>
                  <InputField
                    id="bulkEmails"
                    label={t('UserProfile.externalEmails')}
                    value={bulkEmails}
                    onChange={(e) => setBulkEmails(e.target.value)}
                    placeholder={t('UserProfile.emailsPlaceholder')}
                  />
                  <InputField
                    id="bulkSubject"
                    label={t('UserProfile.subject')}
                    value={bulkSubject}
                    onChange={(e) => setBulkSubject(e.target.value)}
                    required
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">{t('UserProfile.htmlContent')}</label>
                    <textarea
                      value={bulkHtml}
                      onChange={(e) => setBulkHtml(e.target.value)}
                      placeholder={t('UserProfile.htmlPlaceholder')}
                      className="w-full border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white text-black p-2 h-32"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">{t('UserProfile.textContent')}</label>
                    <textarea
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      placeholder={t('UserProfile.textPlaceholder')}
                      className="w-full border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white text-black p-2 h-24"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full sm:w-auto bg-indigo-600 text-white py-2 px-6 rounded-md font-semibold hover:bg-indigo-700 transition-colors"
                    disabled={sendingBulk}
                  > 
                    {sendingBulk ? t('UserProfile.sending') : t('UserProfile.sendEmail')}
                  </button>
                </form>
                {bulkResult && (
                  <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700">
                    {t('UserProfile.bulkResult', { total: bulkResult.total, sent: bulkResult.sent, failed: bulkResult.failed })}
                  </div>
                )}
              </SettingsCard>
            )}
 
          </main>
        </div>
      </div>
    </div>
  );
};
 
export default UserProfile;